"use client";

import { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Flex, Stack, Text } from "@chakra-ui/react";
import Link from "next/link";
import { getConsent, setConsent } from "@/lib/analytics";
import {
  releaseBodyPaddingBottom,
  setBodyPaddingBottom,
} from "@/lib/layout/body-padding-bottom";

/**
 * First-visit cookie consent banner.
 *
 * Honours the promise in the cookie policy (§5.2): "For functional and
 * analytics cookies, we will request your consent when you first visit our
 * website." Offers an explicit Accept and an explicit Decline; the choice is
 * persisted (see `CONSENT_STORAGE_KEY`) and the banner never reappears once a
 * choice is recorded. Declining opts PostHog out of capturing via the consent
 * event the analytics provider listens to.
 *
 * Hydration: reading localStorage during render would make the server markup
 * (always "no banner") disagree with the client. Instead we render `null`
 * until after mount, then decide from localStorage in an effect. First paint
 * is therefore identical on server and client — no mismatch — and the banner
 * only appears on the subsequent client render when no choice exists.
 */
export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getConsent() === null) {
      setIsVisible(true);
    }
  }, []);

  // A fixed banner occupies no space in flow, so without a reservation it
  // paints over whatever is last on the page — measured on Rome, over the
  // footer's "Privacy inquiries" and copyright rows. It registers its measured
  // height with the single body-padding owner rather than writing `body` style
  // itself, so it never races the purchase bar's reservation on the transition
  // between them.
  //
  // Measured, not hardcoded: the banner is 75px on one desktop row but wraps to
  // 267px at 320px wide (text over several lines, buttons stacked). Recomputed
  // via a ResizeObserver because that height is width-dependent and can change
  // without `isVisible` changing — a phone rotated re-wraps the banner taller.
  //
  // The write is deferred to the next frame, mirroring the purchase bar: growing
  // `body` padding-bottom can add a vertical scrollbar, which narrows the
  // viewport, which can re-wrap the banner taller and fire the observer again —
  // the "ResizeObserver loop completed with undelivered notifications" path.
  // Writing on the next frame breaks that loop.
  useEffect(() => {
    if (!isVisible) return;
    const el = bannerRef.current;
    if (!el) return;

    const sync = () => {
      const current = bannerRef.current;
      if (current) {
        setBodyPaddingBottom("cookie-consent", current.offsetHeight);
      }
    };
    sync();

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      releaseBodyPaddingBottom("cookie-consent");
    };
  }, [isVisible]);

  const decide = (choice: "accepted" | "declined") => {
    setConsent(choice);
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      ref={bannerRef}
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={90}
      bg="var(--color-surface)"
      borderTopWidth="1px"
      borderColor="var(--color-border)"
      boxShadow="0 -4px 16px rgba(0, 0, 0, 0.06)"
      role="region"
      aria-label="Cookie consent"
    >
      <Container maxW="1200px" mx="auto" py={4} px={{ base: 6, md: 8 }}>
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          gap={4}
        >
          <Text fontSize="sm" color="var(--color-text-secondary)" maxW="720px">
            We use strictly necessary cookies to run the Service and, with your
            consent, functional and analytics cookies to understand how it is
            used. You can accept or decline the optional ones. Read our{" "}
            <Link href="/legal/cookies">
              <Text
                as="span"
                color="var(--color-primary)"
                textDecoration="underline"
                _hover={{ color: "var(--color-primary-hover)" }}
              >
                cookie policy
              </Text>
            </Link>
            .
          </Text>
          <Stack
            direction={{ base: "column", sm: "row" }}
            gap={3}
            flexShrink={0}
          >
            <Button
              variant="ghost"
              color="var(--color-text-secondary)"
              fontWeight="500"
              borderWidth="1px"
              borderColor="var(--color-border)"
              borderRadius="var(--radius-sm)"
              px={5}
              _hover={{ color: "var(--color-text-primary)", bg: "var(--color-surface-hover)" }}
              onClick={() => decide("declined")}
            >
              Decline
            </Button>
            <Button
              bg="var(--color-primary)"
              color="white"
              fontWeight="500"
              borderRadius="var(--radius-sm)"
              px={5}
              _hover={{ bg: "var(--color-primary-hover)" }}
              onClick={() => decide("accepted")}
            >
              Accept
            </Button>
          </Stack>
        </Flex>
      </Container>
    </Box>
  );
}
